import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { LoggerService } from '../services/logger.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;

  // null  = refresh in progress  → other requests queue and wait
  // true  = refresh succeeded    → queued requests retry
  // false = refresh failed       → queued requests receive an error
  private refreshTokenSubject = new BehaviorSubject<boolean | null>(null);

  constructor(
    private authService: AuthService,
    private logger: LoggerService,
    private router: Router,
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const clonedRequest = request.clone({ withCredentials: true });

    return next.handle(clonedRequest).pipe(
      catchError((error: HttpErrorResponse) => {

        // Auth endpoints handle their own errors — never intercept them
        if (this.isAuthEndpoint(request.url)) {
          return throwError(() => error);
        }

        if (error.status === 401) {
          return this.handle401Error(clonedRequest, next);
        }

        if (error.status === 403) {
          this.router.navigate(['/unauthorized']);
        }

        return throwError(() => error);
      }),
    );
  }

  // ─────────────────────────────────────────────────────────
  // Handle 401 — refresh token then retry
  // ─────────────────────────────────────────────────────────
  private handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      this.logger.info('Attempting token refresh...');

      return this.authService.refreshToken().pipe(
        switchMap((response) => {
          this.isRefreshing = false;

          if (response?.success) {
            this.logger.info('Token refreshed, retrying original request');
            this.refreshTokenSubject.next(true);
            return next.handle(request);
          }

          this.logger.warn('Token refresh returned failure response');
          this.refreshTokenSubject.next(false);
          this.authService.logoutLocal();
          return throwError(() => new Error('Token refresh failed'));
        }),
        catchError((error: HttpErrorResponse) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(false);

          // ── Only logout when the refresh token is genuinely invalid ──
          // 401 / 403 → server explicitly rejected the token → session is dead → logout.
          // status 0  → no internet / request never reached server → session may be fine.
          // 5xx       → server error → session may be fine.
          // For non-401/403 errors the authService already scheduled a 30s retry
          // inside refreshToken()'s catchError. We just propagate the error here.
          if (error.status === 401 || error.status === 403) {
            this.logger.warn('Refresh token rejected — logging out');
            this.authService.logoutLocal();
          } else {
            this.logger.warn(`Refresh failed (status ${error.status}) — session preserved, retry scheduled`);
          }

          return throwError(() => error);
        }),
      );
    }

    // Another request already triggered a refresh — queue behind it
    return this.refreshTokenSubject.pipe(
      filter((result) => result !== null),
      take(1),
      switchMap((succeeded) => {
        if (succeeded) {
          return next.handle(request);
        }
        return throwError(() => new Error('Session expired. Please log in again.'));
      }),
    );
  }

  // ─────────────────────────────────────────────────────────
  // Auth endpoints manage their own errors — never intercept them
  // ─────────────────────────────────────────────────────────
  private isAuthEndpoint(url: string): boolean {
    const authEndpoints = [
      '/auth/login',
      '/auth/register',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/refresh-token',
      '/auth/me',
      '/auth/logout',
      '/api/enquiry',
    ];
    return authEndpoints.some((endpoint) => url.includes(endpoint));
  }
}